import { useVerificarOtpLoginUnificadoSpace } from "@/api/space/hooks/useSpace.authenticator";
import useAutenticacaoJwt from "../../../../../useAutenticacaoJwt";
import type { IdentificacaoGrupoLogin } from "../../../../../tiposAutenticacaoJwt";
import { construirInicioLoginSpaceRequest } from "../../construirLoginUnificadoRequest";
import type useBloqueioTentativasLogin from "../../hooks/useBloqueioTentativasLogin";
import useTurnstileLogin from "../../hooks/useTurnstileLogin";
import useFluxoOtpSpaceUnificado from "../../hooks/useFluxoOtpSpaceUnificado";
import { resolverIdGrupoConsultaSpace } from "../../utils/grupoLogin.utils";
import VerificacaoSegurancaLogin from "../VerificacaoSegurancaLogin";
import DialogoAutenticacaoDoisFatores from "../../../../AutenticacaoDoisFatores/DialogoAutenticacaoDoisFatores";
import CampoUsuarioSpaceOtp from "../../../../AutenticacaoDoisFatores/CampoUsuarioSpaceOtp";
import Button from "@mui/material/Button";

// --------------------------------- TYPES ---------------------------------
type DialogoOtpSpaceUnificadoProps = {
	readonly identificacao: IdentificacaoGrupoLogin;
	readonly idGrupoConsulta: string | null;
	readonly bloqueio: ReturnType<typeof useBloqueioTentativasLogin>;
	readonly aoFechar: () => void;
	readonly aoExpirar: () => void;
};

// ------------------------------- COMPONENT -------------------------------
export default function DialogoOtpSpaceUnificado({
	identificacao,
	idGrupoConsulta,
	bloqueio,
	aoFechar,
	aoExpirar,
}: Readonly<DialogoOtpSpaceUnificadoProps>) {
	// -------------------------------- STATES --------------------------------
	const verificacao = useTurnstileLogin();
	const { entrarUnificado } = useAutenticacaoJwt();

	// -------------------------------- QUERIES -------------------------------
	const { mutateAsync } = useVerificarOtpLoginUnificadoSpace();

	const fluxo = useFluxoOtpSpaceUnificado({
		identificacao,
		bloqueio,
		verificacao,
		aoFechar,
		aoExpirar,
		aoIniciarDesafio: (nome, token, signal) => {
			if (!entrarUnificado) throw new Error("Autenticação indisponível.");
			return entrarUnificado({
				payload: construirInicioLoginSpaceRequest(identificacao, nome, token),
				identificacao,
				signal,
			});
		},

		aoConfirmarOtp: ({ desafio, codigo, token, signal }) =>
			mutateAsync({
				payload: { desafio_otp: desafio, codigo_verificacao: codigo, token_turnstile: token },
				signal,
			}),
	});

	// ----------------------------- DERIVED STATE ----------------------------
	const slug = identificacao.tipo === "slug" ? identificacao.valor : undefined;
	const groupId =
		identificacao.tipo === "codigo" ? (idGrupoConsulta ?? resolverIdGrupoConsultaSpace(identificacao.valor)) : null;
	const grupoDisponivel = Boolean(slug || groupId);
	const codigoDisponivel = Boolean(fluxo.desafio && (verificacao.token || fluxo.enviando));
	const preparando = fluxo.nomeUsuario !== null && !codigoDisponivel && !fluxo.inicioFalhou;

	// -------------------------------- RETURN --------------------------------
	return (
		<DialogoAutenticacaoDoisFatores
			aberto
			desabilitarFechamentoBackdrop
			carregando={fluxo.enviando && fluxo.desafio !== null}
			mensagemErro={fluxo.erroExibido}
			verificacaoDisponivel={!bloqueio.estaBloqueado()}
			revisaoCodigo={fluxo.revisaoCodigo}
			identificacaoPersonalizada={{
				chave: fluxo.nomeUsuario ?? "",
				disponivel: fluxo.nomeUsuario !== null && grupoDisponivel && codigoDisponivel,
				campo: (
					<CampoUsuarioSpaceOtp
						aberto
						permitirBuscaUsuarioPorCodigo
						groupId={groupId ?? undefined}
						slug={slug}
						rotulo="Nome ou ID do usuário"
						placeholder="Digite seu nome / ID de usuário"
						carregando={fluxo.enviando || bloqueio.estaBloqueado()}
						aoValidar={fluxo.validarNome}
						consulta={{
							contexto: "login",
							habilitada: grupoDisponivel && (fluxo.nomeUsuario !== null || !bloqueio.estaBloqueado()),
							aoErro: bloqueio.registrarErro,
						}}
					/>
				),
			}}
			aoFechar={aoFechar}
			aoVerificar={fluxo.verificar}
			aoCompletarCodigo={fluxo.verificar}
			aoAlterarCodigo={fluxo.alterarCodigo}
			conteudoAdicional={
				<div className="mt-4">
					{preparando ? (
						<output className="mb-2 block text-xs text-center text-muted-foreground">
							Preparando verificação…
						</output>
					) : null}
					{fluxo.inicioFalhou ? (
						<Button onClick={fluxo.tentarNovamente} disabled={bloqueio.estaBloqueado()}>
							Tentar novamente
						</Button>
					) : null}
					<VerificacaoSegurancaLogin
						verificacao={verificacao}
						carregando={fluxo.enviando}
						action={fluxo.desafio ? "login_otp" : "login"}
					/>
				</div>
			}
		/>
	);
}
